import {
  Listbox,
  ListboxItem,
  type ListboxItemSlots,
  type ListboxProps,
  type SharedSelection,
  type SlotsToClasses,
} from '@heroui/react';
import type { Control, FieldValues, Path, PathValue } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { isEmpty } from '@/utils/object';
import { toaster } from '../../index';

type ListboxItemData = {
  key: string;
  description?: string;
  shortcut?: string;
  startContent?: React.ReactNode;
  title: React.ReactNode;
  color?: ListboxProps['color'];
  className?: string;
  showDivider?: boolean;
  endContent?: React.ReactNode;
  classNames?: SlotsToClasses<ListboxItemSlots>;
  children?: React.ReactNode;
};

export interface FormListBoxProps<T extends FieldValues> extends Omit<ListboxProps, 'children'> {
  name: Path<T>;
  control: Control<T, unknown>;
  onValueChange?: (value: PathValue<T, Path<T>>) => void;
  transform?: (val: string) => PathValue<T, Path<T>>;
  showSelected?: boolean;
  transformSelected?: (val: string) => string;
  showError?: boolean;
  maxSelected?: number;
  shouldUnregister?: boolean;
  options: ListboxItemData[];
}

export const FormListBox = <T extends FieldValues>({
  control,
  onValueChange,
  name,
  transform,
  showSelected,
  showError,
  maxSelected,
  shouldUnregister,
  selectionMode = 'single',
  options,
  ...rest
}: FormListBoxProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, onBlur, value } }) => {
        const onHandleChange = (keys: SharedSelection) => {
          if (selectionMode === 'multiple') {
            const val = keys ? Array.from(keys) : [];
            if (maxSelected && val.length > maxSelected) {
              toaster.warning(`You can only select up to ${maxSelected} items`);
              return;
            }
            if (transform && val.length > 0) {
              const newVal = val.map(transform as never);
              onChange(newVal);
              if (onValueChange) {
                // onValueChange(newVal as PathValue<T, Path<T>>);
                onValueChange(newVal as never);
              }
              return;
            }
            if (onValueChange) {
              onValueChange(val as never);
            }
            onChange(val);
            return;
          }
          const val = keys ? Array.from(keys).join('') : keys;
          if (transform && !isEmpty(val)) {
            const newVal = transform(val);
            onChange(newVal);
            if (onValueChange) {
              onValueChange(newVal);
            }
            return;
          }
          if (onValueChange) {
            onValueChange(val as never);
          }
          onChange(val);
        };
        return (
          <Listbox
            {...rest}
            selectedKeys={
              selectionMode === 'multiple'
                ? value
                  ? new Set(value.map(String))
                  : undefined
                : value != null
                  ? [`${value}`]
                  : undefined
            }
            selectionMode={selectionMode}
            className='max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-default-200 scrollbar-track-default-100'
            onSelectionChange={onHandleChange}
            onBlur={onBlur}
          >
            {options.map((record) => (
              <ListboxItem
                key={record.key}
                title={record.title}
                description={record.description}
                showDivider={record.showDivider}
                startContent={record.startContent}
                endContent={record.endContent}
                // className={record.className}
                // classNames={record.classNames}
              />
            ))}
          </Listbox>
        );
      }}
    />
  );
};
